/**
 * Los proyectos que están en su ventana de gracia, con su botón de recuperar.
 *
 * Sin esta pantalla la ventana no existiría para el cliente: sus datos seguirían
 * ahí treinta días y no habría un solo sitio donde enterarse ni cómo pedirlos de
 * vuelta. Un plazo de recuperación que sólo conoce el servidor es lo mismo que
 * un borrado inmediato, pero pagando el almacenamiento.
 *
 * ── Dónde vive, y dónde ya no ───────────────────────────────────────────────
 *
 * Sólo en la pantalla sin proyectos, y DEBAJO de las formas de empezar uno. Ahí
 * es imprescindible: no hay menú lateral, así que sin esto los borrados no se
 * pueden alcanzar desde ninguna parte. Dentro del taller no aparece — allí su
 * sitio es el selector de proyectos, apagados y con su plazo, que es donde se
 * buscan los proyectos y donde no estorban.
 *
 * Debajo y no encima porque no urge: hay treinta días. Quien llega a esta
 * pantalla viene a empezar algo, y un muro de avisos antes de las tres formas de
 * hacerlo convierte lo accesorio en lo primero.
 *
 * ── Una tarjeta, no una banda por proyecto ──────────────────────────────────
 *
 * Cada uno traía su propia banda con el mismo párrafo dentro. Con siete
 * borrados, siete veces la misma frase y lo único distinto era el nombre. Ahora
 * la explicación se dice una vez arriba y cada proyecto ocupa un renglón con lo
 * único que le es propio: cómo se llama y cuánto le queda.
 */
import { useCallback, useEffect, useState } from 'react'
import { IconChevronDown, IconLoader2, IconRotate2, IconTrash } from '@tabler/icons-react'
import { listDeletedProjects, restoreProject, type DeletedProject } from '../lib/api'
import { longDateTime } from '../lib/dates'
import { activeLanguage, useI18n, translateServerError } from '../lib/i18n'

/** Cuántos días quedan, redondeando hacia arriba: «medio día» se lee como cero. */
function daysLeft(purgeAt: string | null): number | null {
  if (!purgeAt) return null
  return Math.max(0, Math.ceil((new Date(purgeAt).getTime() - Date.now()) / 86_400_000))
}

export function DeletedProjectsBanner({ token, onRestored }: {
  token: string
  /** Tras recuperar: la lista de proyectos del panel se quedó vieja. */
  onRestored?: (projectId: string) => void
}) {
  const { t } = useI18n()
  const [projects, setProjects] = useState<DeletedProject[]>([])
  const [expanded, setExpanded] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setProjects(await listDeletedProjects(token))
    } catch {
      /* En silencio: esto es un añadido al panel, no el panel. Que falle su
         consulta no puede llenar de errores la pantalla de alguien que
         probablemente no ha borrado nada. */
    }
  }, [token])

  useEffect(() => { void load() }, [load])

  async function restore(project: DeletedProject) {
    setBusyId(project.id)
    setError(null)
    try {
      await restoreProject(token, project.id)
      setProjects((current) => current.filter((row) => row.id !== project.id))
      onRestored?.(project.id)
    } catch (err) {
      setError(translateServerError(err, t('deleted.restoreFailed')))
      setBusyId(null)
    }
  }

  if (projects.length === 0) return null

  return (
    <section className={`dash-deleted-panel${expanded ? ' is-open' : ''}`}>
      <button
        type="button"
        className="dash-deleted-panel-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
      >
        <span className="dash-deleted-banner-icon" aria-hidden="true"><IconTrash size={15} /></span>
        <strong>
          {projects.length === 1 ? t('deleted.countOne') : t('deleted.countMany', { count: projects.length })}
        </strong>
        <IconChevronDown className="dash-deleted-panel-chevron" size={15} aria-hidden="true" />
      </button>

      {expanded ? (
        <div className="dash-deleted-panel-body">
          {/* La explicación se revela sólo cuando alguien decide revisar sus
              borrados. En reposo, el onboarding conserva una sola jerarquía:
              las formas de empezar un proyecto. */}
          <p className="dash-deleted-panel-explain">
            {t(projects.length === 1 ? 'deleted.explainOne' : 'deleted.explainMany')}
          </p>

          {error ? <p className="dash-deleted-banner-error" role="alert">{error}</p> : null}

          <ul className="dash-deleted-panel-list">
            {projects.map((project) => {
              const left = daysLeft(project.purgeAt)
              const busy = busyId === project.id
              return (
                <li key={project.id}>
              <span className="dash-deleted-panel-mark" aria-hidden="true">
                {/* Con el idioma vigente: hay alfabetos donde la mayúscula de
                    una letra depende de él, y el turco es el ejemplo de manual. */}
                {(project.name.trim().charAt(0) || 'P').toLocaleUpperCase(activeLanguage())}
              </span>

              <span className="dash-deleted-panel-name">{project.name || t('deleted.noName')}</span>

              {/* El plazo en días y no la fecha: «quedan 3 días» se entiende sin
                  calcular, y es cuando de verdad urge. La fecha exacta va en el
                  `title`, para quien necesite planificar. */}
              <span
                className={`dash-deleted-panel-left${left !== null && left <= 3 ? ' is-soon' : ''}`}
                title={project.purgeAt ? longDateTime(project.purgeAt) : undefined}
              >
                {left === null ? t('deleted.soon')
                  : left === 0 ? t('deleted.today')
                    : t(left === 1 ? 'deleted.inDaysOne' : 'deleted.inDaysMany', { count: left })}
              </span>

              <button
                type="button"
                className="dash-secondary-btn dash-deleted-panel-action"
                disabled={busy}
                onClick={() => void restore(project)}
              >
                {busy ? <IconLoader2 size={14} className="dash-spin" /> : <IconRotate2 size={14} />}
                {t(busy ? 'deleted.restoring' : 'deleted.restore')}
              </button>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </section>
  )
}
